import React, { useState, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Stack,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardActionArea,
  CardContent,
  Box,
  MenuItem,
  Alert,
  CircularProgress,
  Collapse,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ConstructionIcon from '@mui/icons-material/Construction';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  searchWorkers,
  createApproval,
  uploadDocument,
  buildWorkerApprovalUrl,
  type WorkerOption,
} from '../../services/documentApprovalApi';
import {
  documentTemplates,
  TEMPLATE_LANGUAGES,
  type DocumentTemplate,
  type TemplateLang,
} from '../../templates/documentTemplates';

const DOCUMENT_TYPES = [
  { value: 'contract', label: 'Contract' },
  { value: 'timesheet', label: 'Timesheet' },
  { value: 'safety_form', label: 'Safety Form' },
  { value: 'id_verification', label: 'ID Verification' },
  { value: 'work_permit', label: 'Work Permit' },
  { value: 'housing_agreement', label: 'Housing Agreement' },
  { value: 'other', label: 'Other' },
];

type SourceMode = 'preset' | 'upload' | 'custom';

function normalizeWhatsAppNumber(value: string | null | undefined): string | null {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('00')) return digits.slice(2);
  if (digits.startsWith('31')) return digits;
  if (digits.startsWith('0')) return `31${digits.slice(1)}`;
  return digits;
}

function getKnownPhone(worker: WorkerOption | null): string {
  return worker?.whatsappId || worker?.mobileNumber || worker?.phoneNumber || '';
}

function buildWhatsAppLink(phoneNumber: string | null | undefined, text: string): string | null {
  const normalizedNumber = normalizeWhatsAppNumber(phoneNumber);
  if (!normalizedNumber) return null;
  return `https://wa.me/${normalizedNumber}?text=${encodeURIComponent(text)}`;
}

function buildWhatsAppText(params: { workerName: string; title: string; approvalUrl: string }): string {
  return `Hello ${params.workerName || 'there'},\n\nPlease review and sign "${params.title}" using this link:\n${params.approvalUrl}`;
}

function getWorkerLabel(w: WorkerOption): string {
  const name = [w.firstName, w.prefix, w.lastName].filter(Boolean).join(' ');
  const parts = [name];
  if (w.easyflexRegistrationNumber) parts.push(`EF ${w.easyflexRegistrationNumber}`);
  if (w.email) parts.push(w.email);
  return parts.join(' — ');
}

const templateIcon = (iconName: string) => {
  switch (iconName) {
    case 'directions_car':
      return <DirectionsCarIcon sx={{ fontSize: 32, color: 'primary.main' }} />;
    case 'construction':
      return <ConstructionIcon sx={{ fontSize: 32, color: 'primary.main' }} />;
    default:
      return null;
  }
};

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  accessToken: string;
}

export const CreateApprovalDialog: React.FC<Props> = ({ open, onClose, onCreated, accessToken }) => {
  // Worker
  const [worker, setWorker] = useState<WorkerOption | null>(null);
  const [workerEmail, setWorkerEmail] = useState('');
  const [workerSearchInput, setWorkerSearchInput] = useState('');
  const [workerOptions, setWorkerOptions] = useState<WorkerOption[]>([]);
  const [workerLoading, setWorkerLoading] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();
  const searchRequestId = useRef(0);

  // Source
  const [sourceMode, setSourceMode] = useState<SourceMode>('preset');
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [customHtml, setCustomHtml] = useState('');

  // Config
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [documentType, setDocumentType] = useState('contract');
  const [expiresAt, setExpiresAt] = useState('');
  const [language, setLanguage] = useState<TemplateLang>('en');

  // UI
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdApprovalUrl, setCreatedApprovalUrl] = useState<string | null>(null);
  const [createdWarnings, setCreatedWarnings] = useState<string[]>([]);
  const [sendEmailOption, setSendEmailOption] = useState(true);
  const [sendWhatsAppOption, setSendWhatsAppOption] = useState(false);
  const [createdWhatsAppLink, setCreatedWhatsAppLink] = useState<string | null>(null);

  const runWorkerSearch = useCallback(
    async (query: string) => {
      const requestId = ++searchRequestId.current;
      setWorkerLoading(true);
      try {
        const results = await searchWorkers(accessToken, query.trim());
        if (requestId === searchRequestId.current) {
          setWorkerOptions(results);
        }
      } catch {
        if (requestId === searchRequestId.current) {
          setWorkerOptions([]);
        }
      } finally {
        if (requestId === searchRequestId.current) {
          setWorkerLoading(false);
        }
      }
    },
    [accessToken],
  );

  const handleWorkerSearch = useCallback(
    (_: unknown, value: string) => {
      setWorkerSearchInput(value);
      clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => {
        void runWorkerSearch(value);
      }, value.trim().length >= 2 ? 300 : 0);
    },
    [runWorkerSearch],
  );

  const handleSelectTemplate = useCallback((tpl: DocumentTemplate) => {
    setSelectedTemplate(tpl);
    setTitle(tpl.title);
    setDocumentType(tpl.documentType);
  }, []);

  const handleSourceModeChange = useCallback((_: unknown, val: SourceMode | null) => {
    if (val) {
      setSourceMode(val);
      setSelectedTemplate(null);
      setUploadedFile(null);
      setCustomHtml('');
      setTitle('');
      setDocumentType('contract');
    }
  }, []);

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file?.type === 'application/pdf') {
      setUploadedFile(file);
      if (!title) setTitle(file.name.replace(/\.pdf$/i, ''));
    }
  }, [title]);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setUploadedFile(file);
        if (!title) setTitle(file.name.replace(/\.pdf$/i, ''));
      }
    },
    [title],
  );

  const previewHtml = useMemo(() => {
    if (sourceMode === 'preset' && selectedTemplate && worker) {
      const name = [worker.firstName, worker.prefix, worker.lastName].filter(Boolean).join(' ');
      return selectedTemplate.generateHtml(name, new Date().toLocaleDateString('nl-NL'), language);
    }
    if (sourceMode === 'custom' && customHtml) return customHtml;
    return null;
  }, [sourceMode, selectedTemplate, worker, customHtml, language]);

  const canSubmit =
    !!worker &&
    !!title &&
    !!documentType &&
    (sourceMode === 'preset' ? !!selectedTemplate : sourceMode === 'upload' ? !!uploadedFile : !!customHtml);

  const handleSubmit = async () => {
    if (!worker || !canSubmit) return;
    setSubmitting(true);
    setError(null);

    try {
      let fileUrl: string | undefined;
      let fileType: string | undefined;
      let htmlContent: string | undefined;

      if (sourceMode === 'upload' && uploadedFile) {
        const uploaded = await uploadDocument(accessToken, uploadedFile);
        fileUrl = uploaded.fileUrl;
        fileType = uploaded.fileType;
      } else if (sourceMode === 'preset' && selectedTemplate) {
        const name = [worker.firstName, worker.prefix, worker.lastName].filter(Boolean).join(' ');
        htmlContent = selectedTemplate.generateHtml(name, new Date().toLocaleDateString('nl-NL'), language);
      } else if (sourceMode === 'custom') {
        htmlContent = customHtml;
      }

      const result = await createApproval(accessToken, {
        title,
        documentType,
        workerId: worker.id,
        description: description || undefined,
        fileUrl,
        fileType,
        htmlContent,
        expiresAt: expiresAt || undefined,
        workerEmail: sendEmailOption ? (workerEmail || undefined) : undefined,
        sendEmail: sendEmailOption,
        templateId: sourceMode === 'preset' && selectedTemplate ? selectedTemplate.id : undefined,
        language: sourceMode === 'preset' ? language : undefined,
      });

      onCreated();
      if (result.warnings?.length) {
        setCreatedWarnings(result.warnings);
      }
      const approvalUrl = buildWorkerApprovalUrl(result);
      if (approvalUrl) {
        setCreatedApprovalUrl(approvalUrl);
        if (sendWhatsAppOption) {
          const link = buildWhatsAppLink(
            getKnownPhone(worker),
            buildWhatsAppText({
              workerName: [worker.firstName, worker.prefix, worker.lastName].filter(Boolean).join(' '),
              title,
              approvalUrl,
            }),
          );
          setCreatedWhatsAppLink(link);
          if (link) {
            window.open(link, '_blank', 'noopener,noreferrer');
          }
        } else {
          setCreatedWhatsAppLink(null);
        }
      } else if (result.warnings?.length) {
        // Stay open to show warnings even without a URL
        setCreatedApprovalUrl('');
        setCreatedWhatsAppLink(null);
      } else {
        handleClose();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to create approval');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setWorker(null);
    setWorkerEmail('');
    setWorkerSearchInput('');
    setWorkerOptions([]);
    setSourceMode('preset');
    setSelectedTemplate(null);
    setUploadedFile(null);
    setCustomHtml('');
    setTitle('');
    setDescription('');
    setDocumentType('contract');
    setExpiresAt('');
    setLanguage('en');
    setShowPreview(false);
    setError(null);
    setCreatedApprovalUrl(null);
    setCreatedWarnings([]);
    setSendEmailOption(true);
    setSendWhatsAppOption(false);
    setCreatedWhatsAppLink(null);
    onClose();
  };

  const knownPhone = getKnownPhone(worker);
  const whatsappReady = Boolean(normalizeWhatsAppNumber(knownPhone));
  const deliveryValid =
    (sendEmailOption || sendWhatsAppOption) &&
    (!sendEmailOption || Boolean(workerEmail.trim())) &&
    (!sendWhatsAppOption || whatsappReady);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>New Document Approval</DialogTitle>
      <DialogContent dividers>
        {createdApprovalUrl != null ? (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {createdWarnings.length > 0 ? (
              createdWarnings.map((w, i) => (
                <Alert key={i} severity="warning">{w}</Alert>
              ))
            ) : (
              <Alert severity="success">
                {sendEmailOption && sendWhatsAppOption
                  ? 'Approval created, email sent, and the WhatsApp share link is ready.'
                  : sendEmailOption
                    ? 'Approval created and email sent.'
                    : 'Approval created. The WhatsApp share link is ready.'}
              </Alert>
            )}
            {createdApprovalUrl ? (
              <>
                <Typography variant="subtitle2">Direct signing link</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    fullWidth
                    size="small"
                    value={createdApprovalUrl}
                    slotProps={{ input: { readOnly: true } }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => navigator.clipboard.writeText(createdApprovalUrl)}
                    title="Copy to clipboard"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Button
                  variant="outlined"
                  href={createdApprovalUrl}
                  target="_blank"
                  rel="noreferrer"
                  startIcon={<OpenInNewIcon />}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Open signing page
                </Button>
                {createdWhatsAppLink && (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      variant="outlined"
                      color="success"
                      href={createdWhatsAppLink}
                      target="_blank"
                      rel="noreferrer"
                      startIcon={<WhatsAppIcon />}
                    >
                      Open WhatsApp
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => navigator.clipboard.writeText(createdWhatsAppLink)}
                      startIcon={<ContentCopyIcon />}
                    >
                      Copy WhatsApp Link
                    </Button>
                  </Stack>
                )}
              </>
            ) : (
              <Alert severity="info">
                No magic link was generated. The worker will not be able to access this document until the issue is resolved.
              </Alert>
            )}
          </Stack>
        ) : (
        <Stack spacing={3} sx={{ pt: 1 }}>
          {/* Worker selection */}
          <div>
            <Typography variant="subtitle2" gutterBottom>
              Worker *
            </Typography>
            <Autocomplete
              options={workerOptions}
              getOptionLabel={getWorkerLabel}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={worker}
              inputValue={workerSearchInput}
              onChange={(_, val) => {
                setWorker(val);
                setWorkerEmail(val?.email || '');
              }}
              onInputChange={handleWorkerSearch}
              onOpen={() => {
                if (!workerOptions.length) {
                  void runWorkerSearch(workerSearchInput);
                }
              }}
              loading={workerLoading}
              filterOptions={(x) => x}
              noOptionsText={workerLoading ? 'Loading workers...' : 'No workers found'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search by name, email, or EF number..."
                  size="small"
                />
              )}
            />
            <TextField
              label="Worker Email"
              size="small"
              fullWidth
              type="email"
              value={workerEmail}
              onChange={(e) => setWorkerEmail(e.target.value)}
              helperText="Override the email address for this approval"
              sx={{ mt: 1 }}
              disabled={!sendEmailOption}
            />
            <TextField
              label="Phone Number Known In System"
              size="small"
              fullWidth
              value={knownPhone}
              slotProps={{ input: { readOnly: true } }}
              helperText={
                knownPhone
                  ? whatsappReady
                    ? 'This number will be used for the WhatsApp share link.'
                    : 'This number could not be converted into a valid WhatsApp share link.'
                  : 'No phone number is currently known for this worker.'
              }
              sx={{ mt: 1 }}
            />
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendEmailOption}
                    onChange={(e) => setSendEmailOption(e.target.checked)}
                  />
                }
                label="Send by email"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendWhatsAppOption}
                    onChange={(e) => setSendWhatsAppOption(e.target.checked)}
                    disabled={!knownPhone}
                  />
                }
                label="Send by WhatsApp"
              />
              <Typography variant="caption" color="text.secondary">
                WhatsApp opens a prefilled message with the direct signing link.
              </Typography>
            </Stack>
            {sendWhatsAppOption && !whatsappReady && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                A WhatsApp link cannot be created until this worker has a reachable phone number.
              </Alert>
            )}
          </div>

          {/* Document source */}
          <div>
            <Typography variant="subtitle2" gutterBottom>
              Document Source
            </Typography>
            <ToggleButtonGroup
              value={sourceMode}
              exclusive
              onChange={handleSourceModeChange}
              size="small"
              sx={{ mb: 2 }}
            >
              <ToggleButton value="preset">Preset Template</ToggleButton>
              <ToggleButton value="upload">Upload PDF</ToggleButton>
              <ToggleButton value="custom">Custom HTML</ToggleButton>
            </ToggleButtonGroup>

            {sourceMode === 'preset' && (
              <Stack direction="row" spacing={2}>
                {documentTemplates.map((tpl) => (
                  <Card
                    key={tpl.id}
                    variant="outlined"
                    sx={{
                      flex: 1,
                      border: selectedTemplate?.id === tpl.id ? '2px solid' : undefined,
                      borderColor: selectedTemplate?.id === tpl.id ? 'primary.main' : undefined,
                    }}
                  >
                    <CardActionArea onClick={() => handleSelectTemplate(tpl)}>
                      <CardContent>
                        <Stack spacing={1} alignItems="center" textAlign="center">
                          {templateIcon(tpl.icon)}
                          <Typography variant="subtitle2">{tpl.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {tpl.description}
                          </Typography>
                          {selectedTemplate?.id === tpl.id && (
                            <CheckCircleOutlineIcon color="primary" fontSize="small" />
                          )}
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>
            )}

            {sourceMode === 'upload' && (
              <Box
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
                sx={{
                  border: '2px dashed',
                  borderColor: uploadedFile ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.light' },
                }}
                onClick={() => document.getElementById('pdf-upload-input')?.click()}
              >
                <input
                  id="pdf-upload-input"
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={handleFileSelect}
                />
                {uploadedFile ? (
                  <Stack alignItems="center" spacing={1}>
                    <UploadFileIcon color="primary" />
                    <Typography variant="body2">{uploadedFile.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Stack>
                ) : (
                  <Stack alignItems="center" spacing={1}>
                    <UploadFileIcon color="action" sx={{ fontSize: 40 }} />
                    <Typography variant="body2" color="text.secondary">
                      Drag &amp; drop a PDF file here, or click to browse
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Max 10 MB
                    </Typography>
                  </Stack>
                )}
              </Box>
            )}

            {sourceMode === 'custom' && (
              <TextField
                multiline
                minRows={6}
                maxRows={12}
                fullWidth
                size="small"
                placeholder="Paste or write HTML content..."
                value={customHtml}
                onChange={(e) => setCustomHtml(e.target.value)}
              />
            )}
          </div>

          {/* Configuration */}
          <div>
            <Typography variant="subtitle2" gutterBottom>
              Configuration
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Title"
                required
                size="small"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <TextField
                label="Description"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  select
                  label="Document Type"
                  size="small"
                  fullWidth
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                >
                  {DOCUMENT_TYPES.map((dt) => (
                    <MenuItem key={dt.value} value={dt.value}>
                      {dt.label}
                    </MenuItem>
                  ))}
                </TextField>
                {sourceMode === 'preset' && (
                  <TextField
                    select
                    label="Worker Language"
                    size="small"
                    fullWidth
                    value={language}
                    onChange={(e) => setLanguage(e.target.value as TemplateLang)}
                  >
                    {TEMPLATE_LANGUAGES.map((l) => (
                      <MenuItem key={l.code} value={l.code}>
                        {l.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  label="Expires at"
                  type="date"
                  size="small"
                  fullWidth
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Stack>
            </Stack>
          </div>

          {/* Preview */}
          {previewHtml && (
            <div>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2">Preview</Typography>
                <IconButton size="small" onClick={() => setShowPreview(!showPreview)}>
                  {showPreview ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>
              <Collapse in={showPreview}>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mt: 1,
                    maxHeight: 400,
                    overflow: 'auto',
                    bgcolor: '#fff',
                  }}
                >
                  <iframe
                    srcDoc={previewHtml}
                    title="Document preview"
                    sandbox=""
                    style={{ width: '100%', height: 400, border: 'none' }}
                  />
                </Box>
              </Collapse>
            </div>
          )}

          {sourceMode === 'upload' && uploadedFile && (
            <Typography variant="body2" color="text.secondary">
              PDF file: {uploadedFile.name} ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
            </Typography>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {createdApprovalUrl != null ? (
          <Button variant="contained" onClick={handleClose}>Close</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit || !deliveryValid || submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : undefined}
            >
              {submitting ? 'Sending...' : sendEmailOption && sendWhatsAppOption ? 'Send Email & WhatsApp' : sendWhatsAppOption ? 'Prepare WhatsApp' : 'Send to Worker'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
